/* eslint-disable @typescript-eslint/no-explicit-any */
import baseAxios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { getBaseUrl } from '@/libs/env';
import { getAccessToken } from '@/libs/firebaseAuth';
import { handleAuthorizationError } from '@/libs/apiErrorHandler';
import { error as notifyError } from '@/libs/notification';
import router from '@/router';

export type AxiosWhitelist = readonly string[];

function asStringMessage(data: unknown): string | null {
  if (typeof data === 'string') return data;

  if (Array.isArray(data)) {
    for (const item of data) {
      const nested = asStringMessage(item);
      if (nested) return nested;
    }
    return null;
  }

  if (data && typeof data === 'object') {
    const candidates = [
      (data as any).message,
      (data as any).error,
      (data as any).detail
    ];
    for (const candidate of candidates) {
      if (typeof candidate === 'string') return candidate;
    }
  }

  return null;
}

function isWhitelistedMessage(
  message: string,
  prefixes: AxiosWhitelist
): boolean {
  if (!message) return false;
  if (!prefixes?.length) return false;
  return prefixes.some((p) => !!p && message.startsWith(p));
}

export interface AxiosMeta {
  whitelistPrefixes?: AxiosWhitelist;
  suppressNotify?: boolean;
}

export type AxiosRequestConfigWithMeta<D = any> = AxiosRequestConfig<D> & {
  meta?: AxiosMeta;
};

export interface HandleAxiosClientErrorOptions {
  whitelistPrefixes?: AxiosWhitelist;
  suppressNotify?: boolean;
}

const NETWORK_ERROR_MESSAGE =
  '通信に失敗しました。ネットワーク環境をご確認のうえ、再度お試しください。';

/**
 * Common handler for client-side request failures (call from catch after reject).
 * No-response toasts are shown in the axios response interceptor.
 * 4xx (except 401): whitelist body message or notifyError(status).
 * 401 and 5xx: handled in the interceptor (no toast here).
 */
export function handleAxiosClientError(
  e: unknown,
  options: HandleAxiosClientErrorOptions = {}
) {
  if (options.suppressNotify) return;

  const res = (e as any)?.response;
  const status = res?.status;

  if (status === 401 || (status != null && status >= 500)) return;

  if (res == null) return;

  const whitelistPrefixes = options.whitelistPrefixes ?? [];
  const message = asStringMessage(res.data);

  if (message && isWhitelistedMessage(message, whitelistPrefixes)) {
    notifyError(String(status), message);
    return;
  }

  notifyError(String(status));
}

export function createAxios(): AxiosInstance {
  const instance = baseAxios.create({
    baseURL: getBaseUrl()
  });

  instance.interceptors.request.use(async (config) => {
    const token = await getAccessToken();
    config.headers = config.headers ?? {};
    (config.headers as any).auth = token;
    return config;
  });

  instance.interceptors.response.use(
    (res) => res,
    async (e) => {
      const res = (e as any).response;
      const status = res?.status;

      if (!res) {
        notifyError('-', NETWORK_ERROR_MESSAGE);
        return Promise.reject(e);
      }

      if (status === 401) {
        await handleAuthorizationError();
        return Promise.reject(e);
      }
      if (status >= 500) {
        if (router.currentRoute.value.path !== '/error/server') {
          await router.push('/error/server');
        }
        return Promise.reject(e);
      }

      return Promise.reject(e);
    }
  );

  return instance;
}

// Configured axios instance for the app
export const axios = createAxios();
